// src/db/database.js
import Dexie from 'dexie';

const db = new Dexie('TaalumaERP');

// Version 4 - Added pendingChanges store for offline operations
db.version(4).stores({
  products: 'id, name, sku, category',
  customers: 'id, name, email, phone',
  orders: 'id, customer_id, order_date, status',
  order_items: '++id, order_id, product_id',
  warehouses: 'id, name',
  stocks: 'id, product, warehouse, product_id, warehouse_id',
  sync_queue: '++id, entity_type, entity_id, action, synced, timestamp',
  pendingChanges: '++id, type, storeName, itemId, timestamp', // ⭐ NEW for offline deletes
  settings: 'key',
  subscription: 'id',
  team_members: 'id, username, email'
});

export const dbHelpers = {
  // Initialize DB
  async initDB() {
    return db;
  },

  // ⭐ NEW: Mark items for deletion when offline
  async markForDeletion(storeName, itemId) {
    await db.pendingChanges.add({
      type: 'DELETE',
      storeName,
      itemId,
      timestamp: Date.now()
    });
    // console.log(`✅ Marked ${storeName}:${itemId} for deletion`);
  },

  // Products
  async saveProducts(products) {
    await db.products.clear();
    await db.products.bulkPut(products);
  },

  async getProducts() {
    return await db.products.toArray();
  },

  async getProductById(id) {
    let product = await db.products.get(id);
    if (!product) {
      const allProducts = await db.products.toArray();
      product = allProducts.find(p => String(p.id) === String(id));
    }
    return product;
  },

  async addProduct(product) {
    const id = Date.now();
    await db.products.add({ ...product, id });
    await this.queueSync('product', id, 'create', product);
    return id;
   },

  async updateProduct(id, productData) {
    const existing = await db.products.get(id);
    const updated = { ...existing, ...productData, updated_at: Date.now() };
    await db.products.put(updated);
    
    if (!navigator.onLine) {
      await this.queueSync('product', id, 'update', productData);
    }
    
    return updated;
  },

  // ⭐ NEW: Delete product from IndexedDB
  async deleteProduct(id) {
    await db.products.delete(id);
    // console.log(`✅ Deleted product ${id} from IndexedDB`);
  },

    

  // Stocks
  async saveStocks(stocks) {
    await db.stocks.clear();
    await db.stocks.bulkPut(stocks);
  },

  async getStocks() {
    return await db.stocks.toArray();
  },

  async getStocksByProduct(productId) {
    const byProduct = await db.stocks.where('product').equals(productId).toArray();
    if (byProduct.length > 0) return byProduct;
    
    const byProductId = await db.stocks.where('product_id').equals(productId).toArray();
    return byProductId;
  },

  async updateStock(id, updates) {
    await db.stocks.update(id, updates);
    
    if (!navigator.onLine) {
      await this.queueSync('stock', id, 'update', updates);
    }
  },

  // ⭐ NEW: Delete stock from IndexedDB
  async deleteStock(id) {
    await db.stocks.delete(id);
    // console.log(`✅ Deleted stock ${id} from IndexedDB`);
  },

  async saveStockTransfer(transfer) {
    const id = Date.now();
    await this.queueSync('stock_transfer', id, 'create', transfer);
    return id;
  },

  // Customers
  async saveCustomers(customers) {
      await db.customers.clear();
    await db.customers.bulkPut(customers);
  },
  
  async getCustomers() {
    return await db.customers.toArray();
  },
  
  async getCustomerById(id) {
    let customer = await db.customers.get(id);
    if (!customer) {
      const allCustomers = await db.customers.toArray();
      customer = allCustomers.find(c => String(c.id) === String(id));
    }
    return customer;
  },
  
  async addCustomer(customer) {
    const id = Date.now();
    await db.customers.add({ ...customer, id });
    await this.queueSync('customer', id, 'create', customer);
    return id;
  },
  
  async updateCustomer(id, customerData) {
    const existing = await db.customers.get(id);
    const updated = { ...existing, ...customerData, id, updated_at: Date.now() };
    await db.customers.put(updated);
    
    if (!navigator.onLine) {
      await this.queueSync('customer', id, 'update', customerData);
    }
    
    return updated;
  },
  
  // ⭐ NEW: Delete customer from IndexedDB
  async deleteCustomer(id) {
    await db.customers.delete(id);
    // console.log(`✅ Deleted customer ${id} from IndexedDB`);
  },

  // Orders
  async saveOrders(orders) {
    const ordersWithSync = orders.map(order => ({
      ...order,
      synced: true,
      last_synced: Date.now()
    }));
    await db.orders.bulkPut(ordersWithSync);
  },

  async getOrders() {
    return await db.orders.toArray();
  },

  async getOrderById(id) {
    let order = await db.orders.get(id);
    if (!order) {
      const allOrders = await db.orders.toArray();
      order = allOrders.find(o => String(o.id) === String(id));
    }
    return order;
  },

  async saveOrder(order, items) {
    const orderId = Date.now();
    const orderWithId = {
      ...order,
      id: orderId,
      synced: false,
      created_at: Date.now()
    };
    
    await db.orders.add(orderWithId);
    
    for (const item of items) {
      await db.order_items.add({
        order_id: orderId,
        ...item
      });
    }
    
    await this.queueSync('order', orderId, 'create', { order: orderWithId, items });
    return orderId;
  },

  async updateOrder(id, orderData) {
    const existing = await db.orders.get(id);
    const updated = { ...existing, ...orderData, updated_at: Date.now() };
    await db.orders.put(updated);
    
    if (!navigator.onLine) {
      await this.queueSync('order', id, 'update', orderData);
    }
    
    return updated;
  },

  // ⭐ NEW: Delete order from IndexedDB
  async deleteOrder(id) {
    await db.orders.delete(id);
    // console.log(`✅ Deleted order ${id} from IndexedDB`);
  },

  // Order Items
  async saveOrderItems(items) {
    await db.orders.clear();
    await db.order_items.bulkPut(items);
  },

  async getOrderItems(orderId) {
    return await db.order_items.where('order_id').equals(orderId).toArray();
  },

  // Team Members
  async saveTeamMembers(teamMembers) {
    await db.team_members.clear();
    await db.team_members.bulkPut(teamMembers);
  },

  async getTeamMembers() {
    return await db.team_members.toArray();
  },

  async saveTeamMember(member) {
    await db.team_members.clear();
    await db.team_members.put(member);
  },

  async updateTeamMember(id, memberData) {
    const existing = await db.team_members.get(id);
    const updated = { ...existing, ...memberData, id, updated_at: Date.now() };
    await db.team_members.put(updated);
    
    if (!navigator.onLine) {
      await this.queueSync('team_members', id, 'update', memberData);
    }
    
    return updated;
  },

  async deleteTeamMember(id) {
    await db.team_members.delete(id);
    
    if (!navigator.onLine) {
      await this.queueSync('team_members', id, 'delete');
    }
  },

  // Warehouses
  async saveWarehouses(warehouses) {
    await db.warehouses.clear();
    await db.warehouses.bulkPut(warehouses);
  },

  async getWarehouses() {
    return await db.warehouses.toArray();
  },

  async getWarehouseById(id) {
    let warehouse = await db.warehouses.get(id);
    if (!warehouse) {
      const allWarehouses = await db.warehouses.toArray();
      warehouse = allWarehouses.find(w => String(w.id) === String(id));
    }
    return warehouse;
  },

  // ⭐ NEW: Delete warehouse from IndexedDB
  async deleteWarehouse(id) {
    await db.warehouses.delete(id);
    // console.log(`✅ Deleted warehouse ${id} from IndexedDB`);
  },

  // Sync Queue
  async queueSync(entityType, entityId, action, data = null) {
    await db.sync_queue.add({
      entity_type: entityType,
      entity_id: entityId,
      action: action,
      data: data,
      synced: false,
      timestamp: Date.now()
    });
    // console.log(`📝 Queued for sync: ${action} ${entityType} ${entityId}`);
  },

  async getUnsyncedItems() {
    try {
      return await db.sync_queue.filter(item => item.synced === false).toArray();
    } catch (error) {
      // console.error('Error getting unsynced items:', error);
      return [];
    }
  },

  async markAsSynced(ids) {
    for (const id of ids) {
      await db.sync_queue.update(id, { synced: true });
    }
  },

  async clearSyncQueue() {
    await db.sync_queue.clear();
  },

  // ⭐ NEW: Get pending changes (including deletes)
  async getPendingChanges() {
    return await db.pendingChanges.toArray();
  },

  // ⭐ NEW: Clear pending change after sync
  async clearPendingChange(id) {
    await db.pendingChanges.delete(id);
  },

  // Settings
  async getSetting(key, defaultValue = null) {
    const setting = await db.settings.get(key);
    return setting ? setting.value : defaultValue;
  },

  async setSetting(key, value) {
    await db.settings.put({ key, value });
  },

  // Subscription
  async saveSubscription(subscription) {
    await db.settings.put({ 
      key: 'subscription', 
      value: subscription 
    });
    await db.settings.put({ 
      key: 'lastSync', 
      value: Date.now() 
    });
  },

  async getSubscription() {
    const sub = await db.settings.get('subscription');
    return sub ? sub.value : null;
  },

  // Clear all data (for logout)
  async clearAllData() {
    await db.products.clear();
    await db.stocks.clear();
    await db.customers.clear();
    await db.orders.clear();
    await db.order_items.clear();
    await db.warehouses.clear();
    await db.team_members.clear();
    await db.sync_queue.clear();
    await db.pendingChanges.clear(); // ⭐ NEW
    await db.settings.clear();
  }
};

export default db;


// src/db/database.js
// import Dexie from 'dexie';

// const db = new Dexie('TaalumaERP');

// // Version 4 - Added pendingChanges store for offline operations
// db.version(4).stores({
//   products: 'id, name, sku, category',
//   customers: 'id, name, email, phone',
//   orders: 'id, customer_id, order_date, status',
//   order_items: '++id, order_id, product_id',
//   warehouses: 'id, name',
//   stocks: 'id, product, warehouse, product_id, warehouse_id',
//   sync_queue: '++id, entity_type, entity_id, action, synced, timestamp',
//   pendingChanges: '++id, type, storeName, itemId, timestamp',
//   settings: 'key',
//   subscription: 'id',
//   team_members: 'id, username, email'
// });

// export const dbHelpers = {
//   // Initialize DB
//   async initDB() {
//     return db;
//   },

//   // Mark items for deletion when offline
//   async markForDeletion(storeName, itemId) {
//     await db.pendingChanges.add({
//       type: 'DELETE',
//       storeName,
//       itemId,
//       timestamp: Date.now()
//     });
//     console.log(`✅ Marked ${storeName}:${itemId} for deletion`);
//   },

//   // Products
//   async saveProducts(products) {
//     await db.products.bulkPut(products);
//   },

//   async getProducts() {
//     return await db.products.toArray();
//   },

//   async getProductById(id) {
//     let product = await db.products.get(Number(id));
//     if (!product) {
//       const allProducts = await db.products.toArray();
//       product = allProducts.find(p => String(p.id) === String(id));
//     }
//     return product;
//   },

//   async addProduct(product) {
//     const id = Date.now();
//     await db.products.add({ ...product, id });
//     await this.queueSync('product', id, 'create', product);
//     return id;
//   },

//   async updateProduct(id, productData) {
//     const existing = await db.products.get(Number(id));
//     const updated = { ...existing, ...productData, updated_at: Date.now() };
//     await db.products.put(updated);
    
//     if (!navigator.onLine) {
//       await this.queueSync('product', id, 'update', productData);
//     }
    
//     return updated;
//   },

//   async deleteProduct(productId) {
//     await db.products.delete(Number(productId));
    
//     // Also delete associated stocks
//     const stocks = await db.stocks.toArray();
//     const productStocks = stocks.filter(s => 
//       Number(s.product_id || s.product) === Number(productId)
//     );
    
//     for (const stock of productStocks) {
//       await db.stocks.delete(Number(stock.id));
//     }
    
//     console.log(`✅ Deleted product ${productId} from IndexedDB`);
//   },

//   // Stocks
//   async saveStocks(stocks) {
//     await db.stocks.bulkPut(stocks);
//   },

//   async getStocks() {
//     return await db.stocks.toArray();
//   },

//   async getStocksByProduct(productId) {
//     const byProduct = await db.stocks.where('product').equals(Number(productId)).toArray();
//     if (byProduct.length > 0) return byProduct;
    
//     const byProductId = await db.stocks.where('product_id').equals(Number(productId)).toArray();
//     return byProductId;
//   },

//   async updateStock(id, updates) {
//     await db.stocks.update(Number(id), updates);
    
//     if (!navigator.onLine) {
//       await this.queueSync('stock', id, 'update', updates);
//     }
//   },

//   async deleteStock(stockId) {
//     await db.stocks.delete(Number(stockId));
//     console.log(`✅ Deleted stock ${stockId} from IndexedDB`);
//   },

//   async saveStockTransfer(transfer) {
//     const id = Date.now();
//     await this.queueSync('stock_transfer', id, 'create', transfer);
//     return id;
//   },

//   // Customers
//   async saveCustomers(customers) {
//     await db.customers.bulkPut(customers);
//   },
  
//   async getCustomers() {
//     return await db.customers.toArray();
//   },
  
//   async getCustomerById(id) {
//     let customer = await db.customers.get(Number(id));
//     if (!customer) {
//       const allCustomers = await db.customers.toArray();
//       customer = allCustomers.find(c => String(c.id) === String(id));
//     }
//     return customer;
//   },
  
//   async addCustomer(customer) {
//     const id = Date.now();
//     await db.customers.add({ ...customer, id });
//     await this.queueSync('customer', id, 'create', customer);
//     return id;
//   },
  
//   async updateCustomer(id, customerData) {
//     const existing = await db.customers.get(Number(id));
//     const updated = { ...existing, ...customerData, id, updated_at: Date.now() };
//     await db.customers.put(updated);
    
//     if (!navigator.onLine) {
//       await this.queueSync('customer', id, 'update', customerData);
//     }
    
//     return updated;
//   },
  
//   async deleteCustomer(id) {
//     await db.customers.delete(Number(id));
//     console.log(`✅ Deleted customer ${id} from IndexedDB`);
//   },

//   // Orders
//   async saveOrders(orders) {
//     const ordersWithSync = orders.map(order => ({
//       ...order,
//       synced: true,
//       last_synced: Date.now()
//     }));
//     await db.orders.bulkPut(ordersWithSync);
//   },

//   async getOrders() {
//     return await db.orders.toArray();
//   },

//   async getOrderById(id) {
//     let order = await db.orders.get(Number(id));
//     if (!order) {
//       const allOrders = await db.orders.toArray();
//       order = allOrders.find(o => String(o.id) === String(id));
//     }
//     return order;
//   },

//   async saveOrder(order, items) {
//     const orderId = Date.now();
//     const orderWithId = {
//       ...order,
//       id: orderId,
//       synced: false,
//       created_at: Date.now()
//     };
    
//     await db.orders.add(orderWithId);
    
//     for (const item of items) {
//       await db.order_items.add({
//         order_id: orderId,
//         ...item
//       });
//     }
    
//     await this.queueSync('order', orderId, 'create', { order: orderWithId, items });
//     return orderId;
//   },

//   async updateOrder(id, orderData) {
//     const existing = await db.orders.get(Number(id));
//     const updated = { ...existing, ...orderData, updated_at: Date.now() };
//     await db.orders.put(updated);
    
//     if (!navigator.onLine) {
//       await this.queueSync('order', id, 'update', orderData);
//     }
    
//     return updated;
//   },

//   async deleteOrder(id) {
//     await db.orders.delete(Number(id));
//     console.log(`✅ Deleted order ${id} from IndexedDB`);
//   },

//   // Order Items
//   async saveOrderItems(items) {
//     await db.order_items.bulkPut(items);
//   },

//   async getOrderItems(orderId) {
//     return await db.order_items.where('order_id').equals(Number(orderId)).toArray();
//   },

//   // Team Members
//   async saveTeamMembers(teamMembers) {
//     await db.team_members.bulkPut(teamMembers);
//   },

//   async getTeamMembers() {
//     return await db.team_members.toArray();
//   },

//   async saveTeamMember(member) {
//     await db.team_members.put(member);
//   },

//   async updateTeamMember(id, memberData) {
//     const existing = await db.team_members.get(Number(id));
//     const updated = { ...existing, ...memberData, id, updated_at: Date.now() };
//     await db.team_members.put(updated);
    
//     if (!navigator.onLine) {
//       await this.queueSync('team_members', id, 'update', memberData);
//     }
    
//     return updated;
//   },

//   async deleteTeamMember(id) {
//     await db.team_members.delete(Number(id));
    
//     if (!navigator.onLine) {
//       await this.queueSync('team_members', id, 'delete');
//     }
//   },

//   // Warehouses
//   async saveWarehouses(warehouses) {
//     await db.warehouses.clear();
//     await db.warehouses.bulkPut(warehouses);
//   },

//   async getWarehouses() {
//     return await db.warehouses.toArray();
//   },

//   async getWarehouseById(id) {
//     let warehouse = await db.warehouses.get(Number(id));
//     if (!warehouse) {
//       const allWarehouses = await db.warehouses.toArray();
//       warehouse = allWarehouses.find(w => String(w.id) === String(id));
//     }
//     return warehouse;
//   },

//   async deleteWarehouse(id) {
//     await db.warehouses.delete(Number(id));
//     console.log(`✅ Deleted warehouse ${id} from IndexedDB`);
//   },

//   // Sync Queue
//   async queueSync(entityType, entityId, action, data = null) {
//     await db.sync_queue.add({
//       entity_type: entityType,
//       entity_id: entityId,
//       action: action,
//       data: data,
//       synced: false,
//       timestamp: Date.now()
//     });
//     console.log(`📝 Queued for sync: ${action} ${entityType} ${entityId}`);
//   },

//   async getUnsyncedItems() {
//     try {
//       return await db.sync_queue.filter(item => item.synced === false).toArray();
//     } catch (error) {
//       console.error('Error getting unsynced items:', error);
//       return [];
//     }
//   },

//   async markAsSynced(ids) {
//     for (const id of ids) {
//       await db.sync_queue.update(id, { synced: true });
//     }
//   },

//   async clearSyncQueue() {
//     await db.sync_queue.clear();
//   },

//   async getPendingChanges() {
//     return await db.pendingChanges.toArray();
//   },

//   async clearPendingChange(id) {
//     await db.pendingChanges.delete(id);
//   },

//   // Settings
//   async getSetting(key, defaultValue = null) {
//     const setting = await db.settings.get(key);
//     return setting ? setting.value : defaultValue;
//   },

//   async setSetting(key, value) {
//     await db.settings.put({ key, value });
//   },

//   // Subscription
//   async saveSubscription(subscription) {
//     await db.settings.put({ 
//       key: 'subscription', 
//       value: subscription 
//     });
//     await db.settings.put({ 
//       key: 'lastSync', 
//       value: Date.now() 
//     });
//   },

//   async getSubscription() {
//     const sub = await db.settings.get('subscription');
//     return sub ? sub.value : null;
//   },

//   // Clear all data (for logout)
//   async clearAllData() {
//     await db.products.clear();
//     await db.stocks.clear();
//     await db.customers.clear();
//     await db.orders.clear();
//     await db.order_items.clear();
//     await db.warehouses.clear();
//     await db.team_members.clear();
//     await db.sync_queue.clear();
//     await db.pendingChanges.clear();
//     await db.settings.clear();
//   }
// };

// export default db;
